import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  FormControl,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostTimelineMutation } from '@/services/queries/team-timeline';
import { PostTeamTeamTimelineRequest } from '@/services/request/team-timeline';
import { TextareaField } from '@/components/TextareaField';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface TeamTimelineCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const TeamTimelineCreateModal = ({ isOpen, onClose, onDone }: TeamTimelineCreateModalProps) => {
  const toast = useToast();

  const postItemMutation = usePostTimelineMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      year: undefined,
      title: '',
      description: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostTeamTeamTimelineRequest = {
        team_uuid: data?.team_uuid,
        year: data.year,
        title: data.title,
        description: data.description,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t(
                'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_team_label',
              )}
              placeholder={t(
                'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t(
                  'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_year_label',
                )}
                {...register('year')}
                maxLength={4}
                rootProps={{ isInvalid: !!errors?.year, errorMessage: errors?.year?.message }}
                onChange={e => {
                  const input = e.target as HTMLInputElement;
                  const value = input.value;

                  // Permitir apenas 4 dígitos numéricos
                  if (/^\d{0,4}$/.test(value)) {
                    input.value = value; // Aceitar somente valores válidos
                  } else {
                    input.value = value.replace(/\D/g, '').slice(0, 4); // Corrigir valores inválidos
                  }
                }}
                flex='1'
              />

              <InputField
                withoutFloating
                label={t(
                  'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_title_label',
                )}
                {...register('title')}
                helperText={t(
                  'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_title_helper',
                )}
                maxLength={50}
                rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
                flex='1'
              />
            </Flex>

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.description}>
              <Text fontWeight='medium'>
                {' '}
                {t(
                  'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_description_label',
                )}
              </Text>
              <TextareaField
                maxLength={200}
                helperText={t(
                  'pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_description_helper',
                )}
                error={errors?.description?.message}
                {...register('description')}
              />
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_cancel_button')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_history_timeline.components.team_timeline_create_modal.team_timeline_create_create_button')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
